import type { InputFrame } from "../net/transport";

export interface InputState {
  pointerLocked: boolean;
  keys: Record<string, boolean>;
  mouseDX: number;
  mouseDY: number;
  peckQueued: boolean;
  peckHeld: boolean;
  flapQueued: boolean;
  pauseQueued: boolean;
  toggleCameraQueued: boolean;
  touchMoveZ: -1 | 0 | 1;
  touchSprint: boolean;
  touchFlapHeld: boolean;
  touchLookX: -1 | 0 | 1;
  touchLookY: -1 | 0 | 1;
}

export interface InputHandlers {
  onRequestPause: () => void;
  onToggleCamera: () => void;
  onPointerLockChange?: (locked: boolean) => void;
  onFirstInteraction?: () => void;
}

const MOUSE_SENSITIVITY = 0.0018;
/** Keyboard look rate when J/L/I/K are held (radians per fixed step at 60 Hz). */
const KEYBOARD_TURN_RATE = 2.4 / 60;
const KEYBOARD_PITCH_RATE = 1.8 / 60;
const TOUCH_FLAP_REPEAT_TICKS = 18;

export class InputController {
  readonly state: InputState = {
    pointerLocked: false,
    keys: {},
    mouseDX: 0,
    mouseDY: 0,
    peckQueued: false,
    peckHeld: false,
    flapQueued: false,
    pauseQueued: false,
    toggleCameraQueued: false,
    touchMoveZ: 0,
    touchSprint: false,
    touchFlapHeld: false,
    touchLookX: 0,
    touchLookY: 0,
  };

  private yaw = 0;
  private pitch = 0;
  private firstInteractionSeen = false;
  private nextTouchFlapTick = 0;
  private moveTouchSeq = 0;
  private readonly moveTouches = new Map<number, { seq: number; z: -1 | 1 }>();

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly handlers: InputHandlers,
  ) {
    this.attach();
  }

  getYaw(): number { return this.yaw; }
  getPitch(): number { return this.pitch; }

  resetLook(): void { this.yaw = 0; this.pitch = 0; }
  resetFirstInteraction(): void { this.firstInteractionSeen = false; }

  requestPointerLock(): void {
    if (!this.state.pointerLocked) this.canvas.requestPointerLock?.();
  }

  releasePointerLock(): void {
    if (document.pointerLockElement === this.canvas) document.exitPointerLock?.();
  }

  /** Drain inputs into a single InputFrame for the current tick. */
  consumeFrame(tick: number): InputFrame {
    const k = this.state.keys;
    const move = {
      x: (k["KeyD"] ? 1 : 0) - (k["KeyA"] ? 1 : 0),
      z: clampAxis((k["KeyS"] ? 1 : 0) - (k["KeyW"] ? 1 : 0) + this.state.touchMoveZ),
    };

    // Keyboard look — J/ArrowLeft turn left, L/ArrowRight turn right,
    // I/ArrowUp look up, K/ArrowDown look down.
    if (k["KeyJ"] || k["ArrowLeft"] || this.state.touchLookX < 0) this.yaw += KEYBOARD_TURN_RATE;
    if (k["KeyL"] || k["ArrowRight"] || this.state.touchLookX > 0) this.yaw -= KEYBOARD_TURN_RATE;
    const half = Math.PI / 2 - 0.05;
    if (k["KeyI"] || k["ArrowUp"] || this.state.touchLookY > 0) {
      this.pitch = Math.min(half, this.pitch + KEYBOARD_PITCH_RATE);
    }
    if (k["KeyK"] || k["ArrowDown"] || this.state.touchLookY < 0) {
      this.pitch = Math.max(-half, this.pitch - KEYBOARD_PITCH_RATE);
    }

    const touchRepeatFlap = this.state.touchFlapHeld && tick >= this.nextTouchFlapTick;
    if (touchRepeatFlap) this.nextTouchFlapTick = tick + TOUCH_FLAP_REPEAT_TICKS;

    const frame: InputFrame = {
      tick,
      move,
      yaw: this.yaw,
      pitch: this.pitch,
      flap: this.state.flapQueued || touchRepeatFlap,
      peck: this.state.peckQueued,
      peckHeld: this.state.peckHeld,
      sprint: !!k["ShiftLeft"] || !!k["ShiftRight"] || this.state.touchSprint,
    };

    this.state.flapQueued = false;
    this.state.peckQueued = false;
    return frame;
  }

  private attach(): void {
    const { canvas } = this;

    canvas.addEventListener("click", () => {
      this.engagePlayInput();
    });

    document.addEventListener("pointerlockchange", () => {
      const locked = document.pointerLockElement === canvas;
      this.state.pointerLocked = locked;
      this.handlers.onPointerLockChange?.(locked);
    });

    document.addEventListener("mousemove", (e) => {
      if (!this.state.pointerLocked) return;
      this.yaw -= e.movementX * MOUSE_SENSITIVITY;
      this.pitch -= e.movementY * MOUSE_SENSITIVITY;
      const half = Math.PI / 2 - 0.05;
      if (this.pitch > half) this.pitch = half;
      if (this.pitch < -half) this.pitch = -half;
    });

    this.attachTouchControls();

    document.addEventListener("mousedown", (e) => {
      this.engagePlayInput();
      if (!this.state.pointerLocked) return;
      if (e.button === 0) {
        this.state.peckQueued = true;
        this.state.peckHeld = true;
      }
    });
    document.addEventListener("mouseup", (e) => {
      if (e.button === 0) this.state.peckHeld = false;
    });

    document.addEventListener("keydown", (e) => {
      this.engagePlayInput();
      if (e.repeat) return;
      this.state.keys[e.code] = true;
      switch (e.code) {
        case "Space":
          this.state.flapQueued = true;
          e.preventDefault();
          break;
        case "ArrowUp":
        case "ArrowDown":
        case "ArrowLeft":
        case "ArrowRight":
          e.preventDefault();
          break;
        case "KeyE":
          this.state.peckQueued = true;
          this.state.peckHeld = true;
          break;
        case "Escape":
        case "KeyP":
          this.handlers.onRequestPause();
          break;
        case "KeyQ":
          this.handlers.onToggleCamera();
          break;
      }
    });

    document.addEventListener("keyup", (e) => {
      this.state.keys[e.code] = false;
      if (e.code === "KeyE") this.state.peckHeld = false;
    });

    window.addEventListener("blur", () => {
      this.state.keys = {};
      this.state.peckHeld = false;
      this.state.touchMoveZ = 0;
      this.state.touchFlapHeld = false;
      this.state.touchLookX = 0;
      this.state.touchLookY = 0;
      this.moveTouches.clear();
    });
  }

  private attachTouchControls(): void {
    const sprint = document.getElementById("touch-sprint") as HTMLButtonElement | null;
    const forward = document.getElementById("touch-forward") as HTMLButtonElement | null;
    const backward = document.getElementById("touch-backward") as HTMLButtonElement | null;
    const flap = document.getElementById("touch-flap") as HTMLButtonElement | null;
    const peck = document.getElementById("touch-peck") as HTMLButtonElement | null;
    const look = document.getElementById("touch-look");

    sprint?.addEventListener("pointerdown", (evt) => {
      this.prepareTouchEvent(evt);
      this.state.touchSprint = !this.state.touchSprint;
      sprint.classList.toggle("touch-control--active", this.state.touchSprint);
      sprint.setAttribute("aria-pressed", String(this.state.touchSprint));
    });

    this.bindMoveStack(forward?.parentElement, forward, backward);
    this.bindHoldButton(flap, () => {
      this.state.flapQueued = true;
      this.state.touchFlapHeld = true;
      this.nextTouchFlapTick = 0;
    }, () => {
      this.state.touchFlapHeld = false;
    });
    this.bindHoldButton(peck, () => {
      this.state.peckQueued = true;
      this.state.peckHeld = true;
    }, () => {
      this.state.peckHeld = false;
    });

    look?.addEventListener("pointerdown", (evt) => {
      this.prepareTouchEvent(evt);
      look.setPointerCapture(evt.pointerId);
      look.classList.add("touch-control--active");
      this.updateTouchLook(look, evt);
    });
    look?.addEventListener("pointermove", (evt) => {
      if (!look.hasPointerCapture(evt.pointerId)) return;
      evt.preventDefault();
      this.updateTouchLook(look, evt);
    });
    const clearLook = (evt: PointerEvent): void => {
      if (look?.hasPointerCapture(evt.pointerId)) look.releasePointerCapture(evt.pointerId);
      look?.classList.remove("touch-control--active");
      this.state.touchLookX = 0;
      this.state.touchLookY = 0;
    };
    look?.addEventListener("pointerup", clearLook);
    look?.addEventListener("pointercancel", clearLook);
  }

  private bindMoveStack(
    stack: HTMLElement | null | undefined,
    forward: HTMLButtonElement | null,
    backward: HTMLButtonElement | null,
  ): void {
    if (!stack || !forward || !backward) return;

    stack.addEventListener("pointerdown", (evt) => {
      this.prepareTouchEvent(evt);
      stack.setPointerCapture(evt.pointerId);
      this.moveTouches.set(evt.pointerId, {
        seq: ++this.moveTouchSeq,
        z: this.getTouchMoveZ(stack, evt),
      });
      this.updateTouchMoveFromActive(forward, backward);
    });

    stack.addEventListener("pointermove", (evt) => {
      const touch = this.moveTouches.get(evt.pointerId);
      if (!touch) return;
      evt.preventDefault();
      touch.z = this.getTouchMoveZ(stack, evt);
      this.updateTouchMoveFromActive(forward, backward);
    });

    const release = (evt: PointerEvent): void => {
      if (stack.hasPointerCapture(evt.pointerId)) stack.releasePointerCapture(evt.pointerId);
      this.moveTouches.delete(evt.pointerId);
      this.updateTouchMoveFromActive(forward, backward);
    };
    stack.addEventListener("pointerup", release);
    stack.addEventListener("pointercancel", release);
  }

  private getTouchMoveZ(stack: HTMLElement, evt: PointerEvent): -1 | 1 {
    const rect = stack.getBoundingClientRect();
    return evt.clientY < rect.top + rect.height / 2 ? -1 : 1;
  }

  private updateTouchMoveFromActive(
    forward: HTMLButtonElement,
    backward: HTMLButtonElement,
  ): void {
    let active: { seq: number; z: -1 | 1 } | null = null;
    for (const touch of this.moveTouches.values()) {
      if (!active || touch.seq > active.seq) active = touch;
    }
    this.state.touchMoveZ = active?.z ?? 0;
    forward.classList.toggle("touch-control--active", this.state.touchMoveZ === -1);
    backward.classList.toggle("touch-control--active", this.state.touchMoveZ === 1);
  }

  private bindHoldButton(
    button: HTMLButtonElement | null,
    onDown: () => void,
    onUp: () => void,
  ): void {
    if (!button) return;
    button.addEventListener("pointerdown", (evt) => {
      this.prepareTouchEvent(evt);
      button.setPointerCapture(evt.pointerId);
      button.classList.add("touch-control--active");
      onDown();
    });
    const release = (evt: PointerEvent): void => {
      if (button.hasPointerCapture(evt.pointerId)) button.releasePointerCapture(evt.pointerId);
      button.classList.remove("touch-control--active");
      onUp();
    };
    button.addEventListener("pointerup", release);
    button.addEventListener("pointercancel", release);
  }

  private prepareTouchEvent(evt: PointerEvent): void {
    evt.preventDefault();
    this.markFirstInteraction();
  }

  private updateTouchLook(target: HTMLElement, evt: PointerEvent): void {
    const rect = target.getBoundingClientRect();
    const dx = evt.clientX - (rect.left + rect.width / 2);
    const dy = evt.clientY - (rect.top + rect.height / 2);
    if (Math.abs(dx) > Math.abs(dy)) {
      this.state.touchLookX = dx > 0 ? 1 : -1;
      this.state.touchLookY = 0;
    } else {
      this.state.touchLookX = 0;
      this.state.touchLookY = dy > 0 ? -1 : 1;
    }
  }

  private markFirstInteraction(): void {
    if (this.firstInteractionSeen) return;
    this.firstInteractionSeen = true;
    this.handlers.onFirstInteraction?.();
  }

  /**
   * Mirrors the old "first click to play" behavior, but for any user input:
   * mark first interaction and attempt pointer lock.
   */
  private engagePlayInput(): void {
    this.markFirstInteraction();
    // Never capture the cursor while overlays are open.
    if (document.body.dataset["screen"] !== "playing") return;
    this.requestPointerLock();
  }
}

function clampAxis(value: number): -1 | 0 | 1 {
  if (value < 0) return -1;
  if (value > 0) return 1;
  return 0;
}
